import React, { useState, useEffect } from 'react';
import { Plus, Dumbbell, Edit, Trash2, FileText, ChevronDown, ChevronRight, ChevronLeft, CalendarDays, List, X, Search } from 'lucide-react';
import { useApp } from '../context/AppContext';
import { useAuth } from '../context/AuthContext';
import { formatDate, formatPrice, formatPriceInput, parsePriceInput } from '../utils/helpers';
import Modal from '../components/ui/Modal';
import ConfirmDialog from '../components/ui/ConfirmDialog';
import { getPtSessionsForGym, schedulePtSession, cancelPtSession, completePtSession } from '../lib/ptSessions';
import { format, parseISO, isAfter } from 'date-fns';
import { ko } from 'date-fns/locale';

// PT 상품 폼
const PtProductForm = ({ product, onClose }) => {
  const { addPtProduct, updatePtProduct } = useApp();
  const [form, setForm] = useState({ name: product?.name || '', sessions: product?.sessions || 10, price: product?.price || '' });

  const handleSubmit = (e) => {
    e.preventDefault();
    const data = { ...form, sessions: Number(form.sessions), price: Number(form.price) };
    if (product) updatePtProduct(product.id, data);
    else addPtProduct(data);
    onClose();
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div>
        <label className="label">상품명</label>
        <input className="input" value={form.name} onChange={e => setForm(p => ({ ...p, name: e.target.value }))} placeholder="PT 10회" required />
      </div>
      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="label">횟수</label>
          <input type="number" className="input" value={form.sessions} onChange={e => setForm(p => ({ ...p, sessions: e.target.value }))} min="1" required />
        </div>
        <div>
          <label className="label">가격 (원)</label>
          <input type="text" inputMode="numeric" className="input" value={formatPriceInput(form.price)} onChange={e => setForm(p => ({ ...p, price: parsePriceInput(e.target.value) }))} placeholder="500,000" required />
        </div>
      </div>
      <div className="flex justify-end gap-3 pt-2">
        <button type="button" onClick={onClose} className="btn-secondary">취소</button>
        <button type="submit" className="btn-primary">{product ? '수정' : '등록'}</button>
      </div>
    </form>
  );
};

// PT 부여 폼
const PtAssignForm = ({ onClose }) => {
  const { members, trainers, ptProducts, addPtAssignment } = useApp();
  const [memberSearch, setMemberSearch] = useState('');
  const [form, setForm] = useState({ memberId: '', productId: '', trainerId: '', price: '', paymentMethod: '', cashReceipt: '' });
  const CASH_RECEIPT_METHODS = ['현금', '계좌이체'];

  const selectedMember = members.find(m => m.id === form.memberId);
  const selectedProduct = ptProducts.find(p => p.id === form.productId);
  const selectedTrainer = trainers.find(t => t.id === form.trainerId);

  const filteredMembers = members.filter(m =>
    !memberSearch || m.name?.includes(memberSearch) || m.phone?.includes(memberSearch)
  );

  const handleProductChange = (productId) => {
    const p = ptProducts.find(p => p.id === productId);
    setForm(prev => ({ ...prev, productId, price: p?.price || '' }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!form.memberId || !form.productId || !form.trainerId) return;
    addPtAssignment({
      memberId: form.memberId,
      productId: form.productId,
      productName: selectedProduct?.name || '',
      trainerId: form.trainerId,
      trainerName: selectedTrainer?.name || '',
      totalSessions: selectedProduct?.sessions || 0,
      usedSessions: 0,
      price: Number(form.price),
      paymentMethod: form.paymentMethod || null,
      cashReceipt: CASH_RECEIPT_METHODS.includes(form.paymentMethod) ? (form.cashReceipt || null) : null,
      status: 'active',
    });
    onClose();
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div>
        <label className="label">회원</label>
        {selectedMember ? (
          <div className="flex items-center justify-between px-3 py-2 rounded-lg bg-primary-50 dark:bg-primary-900/20 border border-primary-200 dark:border-primary-800">
            <span className="text-sm font-medium text-primary-700 dark:text-primary-300">
              {selectedMember.name} ({selectedMember.phone})
            </span>
            <button type="button" onClick={() => { setForm(p => ({ ...p, memberId: '' })); setMemberSearch(''); }} className="text-xs text-gray-400 hover:text-red-500">✕</button>
          </div>
        ) : (
          <>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                className="input pl-9"
                placeholder="이름 또는 전화번호 검색..."
                value={memberSearch}
                onChange={e => setMemberSearch(e.target.value)}
              />
            </div>
            {memberSearch && (
              <div className="mt-1 border border-gray-200 dark:border-gray-600 rounded-lg overflow-hidden max-h-40 overflow-y-auto">
                {filteredMembers.length === 0 ? (
                  <p className="text-sm text-gray-400 text-center py-3">검색 결과가 없습니다.</p>
                ) : (
                  filteredMembers.map(m => (
                    <button
                      key={m.id}
                      type="button"
                      onClick={() => { setForm(p => ({ ...p, memberId: m.id })); setMemberSearch(''); }}
                      className="w-full text-left px-3 py-2 text-sm hover:bg-gray-50 dark:hover:bg-gray-700 border-b border-gray-100 dark:border-gray-700 last:border-0"
                    >
                      <span className="font-medium text-gray-900 dark:text-white">{m.name}</span>
                      <span className="text-gray-400 ml-2">{m.phone}</span>
                    </button>
                  ))
                )}
              </div>
            )}
          </>
        )}
      </div>
      <div>
        <label className="label">PT 상품</label>
        <select className="input" value={form.productId} onChange={e => handleProductChange(e.target.value)} required>
          <option value="">상품 선택</option>
          {ptProducts.map(p => <option key={p.id} value={p.id}>{p.name} - {formatPrice(p.price)}</option>)}
        </select>
      </div>
      <div>
        <label className="label">담당 트레이너</label>
        <select className="input" value={form.trainerId} onChange={e => setForm(p => ({ ...p, trainerId: e.target.value }))} required>
          <option value="">트레이너 선택</option>
          {trainers.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
        </select>
      </div>
      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="label">결제 금액 (원)</label>
          <input type="text" inputMode="numeric" className="input" value={formatPriceInput(form.price)} onChange={e => setForm(p => ({ ...p, price: parsePriceInput(e.target.value) }))} placeholder="0" />
        </div>
        <div>
          <label className="label">결제 수단 (선택)</label>
          <select className="input" value={form.paymentMethod} onChange={e => setForm(p => ({ ...p, paymentMethod: e.target.value, cashReceipt: '' }))}>
            <option value="">선택 안함</option>
            {['현금', '카드', '지역화폐', '계좌이체', '기타'].map(m => <option key={m} value={m}>{m}</option>)}
          </select>
        </div>
        {CASH_RECEIPT_METHODS.includes(form.paymentMethod) && (
          <div>
            <label className="label">현금영수증 (선택)</label>
            <select className="input" value={form.cashReceipt} onChange={e => setForm(p => ({ ...p, cashReceipt: e.target.value }))}>
              <option value="">선택 안함</option>
              <option value="발급">발급</option>
              <option value="미발급">미발급</option>
            </select>
          </div>
        )}
      </div>
      <div className="flex justify-end gap-3 pt-2">
        <button type="button" onClick={onClose} className="btn-secondary">취소</button>
        <button type="submit" className="btn-primary" disabled={!form.memberId}>부여</button>
      </div>
    </form>
  );
};

// PT 일지 폼
const PtLogForm = ({ assignment, onClose, editLog }) => {
  const { addPtLog, updatePtLog } = useApp();
  const [form, setForm] = useState({
    date: editLog?.date || formatDate(new Date().toISOString().split('T')[0]).replace(/\./g, '-'),
    content: editLog?.content || '',
    memo: editLog?.memo || '',
  });
  const today = new Date().toISOString().split('T')[0];

  const handleSubmit = (e) => {
    e.preventDefault();
    if (editLog) {
      updatePtLog(editLog.id, { content: form.content, memo: form.memo });
    } else {
      addPtLog({
        ptAssignmentId: assignment.id,
        memberId: assignment.memberId,
        trainerId: assignment.trainerId,
        trainerName: assignment.trainerName,
        date: form.date || today,
        content: form.content,
        memo: form.memo,
        sessionNumber: assignment.usedSessions + 1,
      });
    }
    onClose();
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div className="p-3 bg-gray-50 dark:bg-gray-700/50 rounded-lg text-sm text-gray-600 dark:text-gray-400">
        {assignment.productName} · {assignment.trainerName} 트레이너 · 잔여 {assignment.totalSessions - assignment.usedSessions}회
      </div>
      <div>
        <label className="label">날짜</label>
        <input type="date" className="input" value={form.date || today} onChange={e => setForm(p => ({ ...p, date: e.target.value }))} required />
      </div>
      <div>
        <label className="label">운동 내용</label>
        <textarea
          className="input min-h-[100px] resize-none"
          value={form.content}
          onChange={e => setForm(p => ({ ...p, content: e.target.value }))}
          placeholder="스쿼트 3세트, 데드리프트 3세트..."
          required
          rows={4}
        />
      </div>
      <div>
        <label className="label">메모</label>
        <textarea
          className="input resize-none"
          value={form.memo}
          onChange={e => setForm(p => ({ ...p, memo: e.target.value }))}
          placeholder="특이사항, 다음 목표 등..."
          rows={2}
        />
      </div>
      <div className="flex justify-end gap-3 pt-2">
        <button type="button" onClick={onClose} className="btn-secondary">취소</button>
        <button type="submit" className="btn-primary">{editLog ? '수정' : '수업 완료'}</button>
      </div>
    </form>
  );
};

// PT 할당 카드
const PtAssignmentCard = ({ assignment }) => {
  const { members, ptLogs, deletePtLog } = useApp();
  const [expanded, setExpanded] = useState(false);
  const [isLogFormOpen, setIsLogFormOpen] = useState(false);
  const [editingLog, setEditingLog] = useState(null);
  const [deleteLogConfirm, setDeleteLogConfirm] = useState(null);

  const member = members.find(m => m.id === assignment.memberId);
  const logs = [...ptLogs.filter(l => l.ptAssignmentId === assignment.id)].sort((a, b) => b.date.localeCompare(a.date));
  const remaining = assignment.totalSessions - assignment.usedSessions;
  const progress = (assignment.usedSessions / assignment.totalSessions) * 100;

  return (
    <div className="card p-4">
      <div className="flex items-start justify-between mb-3">
        <div>
          <p className="font-semibold text-gray-900 dark:text-white">{member?.name}</p>
          <p className="text-sm text-gray-500 dark:text-gray-400">{assignment.trainerName} 트레이너 · {assignment.productName}</p>
        </div>
        <span className={`text-sm font-semibold px-2 py-1 rounded-full ${
          remaining <= 0 ? 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400' :
          remaining <= 5 ? 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400' :
          'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400'
        }`}>
          잔여 {remaining}회
        </span>
      </div>
      <div className="w-full h-2 bg-gray-100 dark:bg-gray-700 rounded-full overflow-hidden mb-1">
        <div className="h-full bg-purple-500 rounded-full transition-all" style={{ width: `${progress}%` }} />
      </div>
      <div className="flex items-center justify-between mt-2">
        <span className="text-xs text-gray-400">{assignment.usedSessions}/{assignment.totalSessions}회</span>
        <div className="flex gap-2">
          <button
            onClick={() => setIsLogFormOpen(true)}
            disabled={remaining <= 0}
            className="text-xs btn-primary px-2 py-1 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1"
          >
            <FileText className="w-3 h-3" />
            수업 기록
          </button>
          <button
            onClick={() => setExpanded(!expanded)}
            className="text-xs btn-secondary px-2 py-1 flex items-center gap-1"
          >
            일지 {logs.length}건
            {expanded ? <ChevronDown className="w-3 h-3" /> : <ChevronRight className="w-3 h-3" />}
          </button>
        </div>
      </div>

      {expanded && (
        <div className="mt-3 pt-3 border-t border-gray-100 dark:border-gray-700 space-y-2">
          {logs.length === 0 ? (
            <p className="text-xs text-gray-400 text-center py-2">PT 일지가 없습니다.</p>
          ) : (
            logs.map(log => (
              <div key={log.id} className="bg-gray-50 dark:bg-gray-700/50 p-3 rounded-lg group relative">
                <div className="flex justify-between text-xs text-gray-400 mb-1">
                  <span>{formatDate(log.date)} · {log.sessionNumber}회차</span>
                  <div className="flex gap-1.5 opacity-0 group-hover:opacity-100 transition-all">
                    <button
                      onClick={() => { setEditingLog(log); setIsLogFormOpen(true); }}
                      className="text-gray-400 hover:text-blue-600"
                    >
                      <Edit className="w-3 h-3" />
                    </button>
                    <button
                      onClick={() => setDeleteLogConfirm(log)}
                      className="text-red-400 hover:text-red-600"
                    >
                      <Trash2 className="w-3 h-3" />
                    </button>
                  </div>
                </div>
                <p className="text-sm text-gray-700 dark:text-gray-300">{log.content}</p>
                {log.memo && <p className="text-xs text-gray-400 mt-1">메모: {log.memo}</p>}
              </div>
            ))
          )}
        </div>
      )}

      <Modal isOpen={isLogFormOpen} onClose={() => { setIsLogFormOpen(false); setEditingLog(null); }} title={editingLog ? 'PT 일지 수정' : 'PT 수업 기록'}>
        <PtLogForm assignment={assignment} editLog={editingLog} onClose={() => { setIsLogFormOpen(false); setEditingLog(null); }} />
      </Modal>

      <ConfirmDialog
        isOpen={!!deleteLogConfirm}
        title="PT 일지 삭제"
        message="이 PT 일지를 삭제하시겠습니까? 횟수가 복구됩니다."
        onConfirm={() => { deletePtLog(deleteLogConfirm.id); setDeleteLogConfirm(null); }}
        onCancel={() => setDeleteLogConfirm(null)}
      />
    </div>
  );
};

// PT 일정 예약 폼
const PtScheduleForm = ({ assignments, members, onClose, onSaved }) => {
  const today = new Date().toISOString().split('T')[0];
  const [form, setForm] = useState({ assignmentId: '', date: today, time: '10:00', notes: '' });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.assignmentId) return;
    setLoading(true);
    const scheduledAt = new Date(`${form.date}T${form.time}:00`).toISOString();
    const { error: err } = await schedulePtSession({
      assignmentId: form.assignmentId,
      scheduledAt,
      notes: form.notes,
    });
    setLoading(false);
    if (err) { setError('일정 등록 중 오류가 발생했습니다.'); return; }
    onSaved();
    onClose();
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div>
        <label className="label">PT 배정 선택</label>
        <select className="input" value={form.assignmentId} onChange={e => setForm(p => ({ ...p, assignmentId: e.target.value }))} required>
          <option value="">선택하세요</option>
          {assignments.filter(a => a.status === 'active').map(a => {
            const member = members.find(m => m.id === a.memberId);
            const remaining = a.totalSessions - a.usedSessions;
            return (
              <option key={a.id} value={a.id} disabled={remaining <= 0}>
                {member?.name || '(회원)'} · {a.trainerName} · 잔여 {remaining}회
              </option>
            );
          })}
        </select>
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="label">날짜</label>
          <input type="date" className="input" value={form.date} min={today} onChange={e => setForm(p => ({ ...p, date: e.target.value }))} required />
        </div>
        <div>
          <label className="label">시간</label>
          <input type="time" className="input" value={form.time} onChange={e => setForm(p => ({ ...p, time: e.target.value }))} required />
        </div>
      </div>
      <div>
        <label className="label">메모 (선택)</label>
        <input className="input" value={form.notes} onChange={e => setForm(p => ({ ...p, notes: e.target.value }))} placeholder="특이사항 등..." />
      </div>
      {error && <p className="text-sm text-red-500">{error}</p>}
      <div className="flex justify-end gap-2 pt-1">
        <button type="button" onClick={onClose} className="btn-secondary">취소</button>
        <button type="submit" className="btn-primary" disabled={loading}>{loading ? '등록 중...' : '일정 등록'}</button>
      </div>
    </form>
  );
};

// 주간 캘린더
const WeeklyCalendar = ({ sessions, onSelectSession }) => {
  const [weekOffset, setWeekOffset] = useState(0);

  const todayBase = new Date();
  todayBase.setHours(0, 0, 0, 0);

  const dayOfWeek = todayBase.getDay();
  const monday = new Date(todayBase);
  monday.setDate(todayBase.getDate() - (dayOfWeek === 0 ? 6 : dayOfWeek - 1) + weekOffset * 7);

  const weekDays = Array.from({ length: 7 }, (_, i) => {
    const d = new Date(monday);
    d.setDate(monday.getDate() + i);
    return d;
  });

  const todayStr = format(todayBase, 'yyyy-MM-dd');

  const sessionsByDate = {};
  sessions.forEach(s => {
    const key = format(parseISO(s.scheduledAt), 'yyyy-MM-dd');
    if (!sessionsByDate[key]) sessionsByDate[key] = [];
    sessionsByDate[key].push(s);
  });

  const DAY_KO = ['월', '화', '수', '목', '금', '토', '일'];

  return (
    <div>
      <div className="flex items-center justify-between mb-3">
        <button
          onClick={() => setWeekOffset(w => w - 1)}
          className="p-1.5 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-500"
        >
          <ChevronLeft className="w-4 h-4" />
        </button>
        <div className="flex items-center gap-2">
          <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
            {format(weekDays[0], 'M.d')} – {format(weekDays[6], 'M.d')}
          </span>
          {weekOffset !== 0 && (
            <button
              onClick={() => setWeekOffset(0)}
              className="text-xs px-2 py-0.5 rounded-full bg-primary-100 text-primary-700 dark:bg-primary-900/30 dark:text-primary-400"
            >
              오늘
            </button>
          )}
        </div>
        <button
          onClick={() => setWeekOffset(w => w + 1)}
          className="p-1.5 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-500"
        >
          <ChevronRight className="w-4 h-4" />
        </button>
      </div>

      <div className="overflow-x-auto">
        <div className="grid grid-cols-7 gap-1 min-w-[480px]">
          {weekDays.map((day, i) => {
            const dateKey = format(day, 'yyyy-MM-dd');
            const daySessions = [...(sessionsByDate[dateKey] || [])].sort((a, b) =>
              a.scheduledAt.localeCompare(b.scheduledAt)
            );
            const isToday = dateKey === todayStr;
            const isPast = day < todayBase;
            const isSun = i === 6;
            const isSat = i === 5;

            return (
              <div
                key={dateKey}
                className={`rounded-xl border flex flex-col ${
                  isToday
                    ? 'border-primary-300 dark:border-primary-600 bg-primary-50 dark:bg-primary-900/10'
                    : 'border-gray-200 dark:border-gray-700'
                }`}
              >
                <div className={`px-1 py-1.5 text-center border-b ${
                  isToday ? 'border-primary-200 dark:border-primary-700' : 'border-gray-100 dark:border-gray-700'
                }`}>
                  <p className={`text-[10px] font-medium ${
                    isSun ? 'text-red-400' : isSat ? 'text-blue-400' : 'text-gray-400 dark:text-gray-500'
                  }`}>
                    {DAY_KO[i]}
                  </p>
                  <p className={`text-sm font-bold leading-tight ${
                    isToday ? 'text-primary-600 dark:text-primary-400' :
                    isPast ? 'text-gray-400' : 'text-gray-800 dark:text-gray-200'
                  }`}>
                    {format(day, 'd')}
                  </p>
                </div>
                <div className="p-1 space-y-0.5 min-h-[80px]">
                  {daySessions.map(s => (
                    <button
                      key={s.id}
                      onClick={() => s.status === 'scheduled' && onSelectSession(s)}
                      className={`w-full text-left rounded-lg px-1.5 py-1 text-[11px] transition-opacity ${
                        s.status === 'completed'
                          ? 'bg-green-100 dark:bg-green-900/40 text-green-700 dark:text-green-400 cursor-default'
                          : s.status === 'cancelled'
                          ? 'bg-gray-100 dark:bg-gray-700 text-gray-400 cursor-default'
                          : 'bg-blue-100 dark:bg-blue-900/40 text-blue-700 dark:text-blue-300 hover:opacity-75'
                      }`}
                    >
                      <p className="font-semibold truncate">{format(parseISO(s.scheduledAt), 'HH:mm')} {s.memberName}</p>
                      <p className="truncate opacity-70">{s.trainerName}</p>
                    </button>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

// 세션 상태 배지
const STATUS_BADGE = {
  scheduled:  'bg-blue-50 text-blue-700 dark:bg-blue-900/20 dark:text-blue-300',
  completed:  'bg-green-50 text-green-700 dark:bg-green-900/20 dark:text-green-300',
  cancelled:  'bg-gray-100 text-gray-500 dark:bg-gray-700 dark:text-gray-400',
};
const STATUS_LABEL = { scheduled: '예정', completed: '완료', cancelled: '취소' };

const PT = () => {
  const { ptProducts, ptAssignments, deletePtProduct, trainers, members, refreshPtData, gymId } = useApp();
  const { gym } = useAuth();
  const [tab, setTab] = useState('assignments');
  const [isProductFormOpen, setIsProductFormOpen] = useState(false);
  const [isAssignOpen, setIsAssignOpen] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [deleteProductConfirm, setDeleteProductConfirm] = useState(null);
  const [trainerFilter, setTrainerFilter] = useState('all');

  // 일정 관련 상태
  const [viewMode, setViewMode] = useState('list'); // 'list' | 'calendar'
  const [sessions, setSessions] = useState([]);
  const [sessionsLoading, setSessionsLoading] = useState(false);
  const [isScheduleOpen, setIsScheduleOpen] = useState(false);
  const [cancelConfirm, setCancelConfirm] = useState(null);
  const [completeSession, setCompleteSession] = useState(null); // 완료 처리할 세션
  const [completeContent, setCompleteContent] = useState('');
  const [completeMemo, setCompleteMemo] = useState('');
  const [completing, setCompleting] = useState(false);

  const effectiveGymId = gymId ?? gym?.id;

  const loadSessions = async () => {
    if (!effectiveGymId) return;
    setSessionsLoading(true);
    const data = await getPtSessionsForGym(effectiveGymId);
    setSessions(data);
    setSessionsLoading(false);
  };

  useEffect(() => {
    if (tab === 'schedule') loadSessions();
  }, [tab, effectiveGymId]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleCancelSession = async () => {
    if (!cancelConfirm) return;
    await cancelPtSession(cancelConfirm.id);
    setCancelConfirm(null);
    loadSessions();
  };

  const handleCompleteSession = async () => {
    if (!completeSession) return;
    setCompleting(true);
    await completePtSession(completeSession.id, completeContent, completeMemo);
    setCompleting(false);
    setCompleteSession(null);
    setCompleteContent('');
    setCompleteMemo('');
    loadSessions();
    refreshPtData();
  };

  const upcomingSessions = sessions.filter(s => s.status === 'scheduled' && isAfter(parseISO(s.scheduledAt), new Date()));
  const pastSessions = sessions.filter(s => s.status !== 'scheduled' || !isAfter(parseISO(s.scheduledAt), new Date()));

  const activePt = ptAssignments.filter(p => p.status === 'active');
  const filteredPt = trainerFilter === 'all'
    ? activePt
    : activePt.filter(p => p.trainerId === trainerFilter);

  return (
    <div className="space-y-4">
      <div className="flex gap-2 border-b border-gray-200 dark:border-gray-700">
        {[{ id: 'assignments', label: 'PT 현황' }, { id: 'schedule', label: '일정 예약' }, { id: 'products', label: '상품 설정' }].map(t => (
          <button
            key={t.id}
            onClick={() => setTab(t.id)}
            className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
              tab === t.id
                ? 'border-primary-600 text-primary-700 dark:text-primary-400'
                : 'border-transparent text-gray-500 hover:text-gray-700 dark:text-gray-400'
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {tab === 'assignments' && (
        <div className="space-y-4">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div className="flex gap-2 flex-wrap">
              <button
                onClick={() => setTrainerFilter('all')}
                className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${trainerFilter === 'all' ? 'bg-primary-600 text-white' : 'bg-white dark:bg-gray-800 text-gray-600 dark:text-gray-400 border border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700'}`}
              >
                전체 ({activePt.length})
              </button>
              {trainers.map(t => {
                const count = activePt.filter(p => p.trainerId === t.id).length;
                return (
                  <button
                    key={t.id}
                    onClick={() => setTrainerFilter(t.id)}
                    className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${trainerFilter === t.id ? 'bg-primary-600 text-white' : 'bg-white dark:bg-gray-800 text-gray-600 dark:text-gray-400 border border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700'}`}
                  >
                    {t.name} ({count})
                  </button>
                );
              })}
            </div>
            <button onClick={() => setIsAssignOpen(true)} className="btn-primary flex items-center gap-2">
              <Plus className="w-4 h-4" />
              PT 부여
            </button>
          </div>

          {filteredPt.length === 0 ? (
            <div className="card p-12 text-center">
              <Dumbbell className="w-12 h-12 text-gray-300 dark:text-gray-600 mx-auto mb-3" />
              <p className="text-gray-400">진행 중인 PT가 없습니다.</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              {filteredPt.map(pt => <PtAssignmentCard key={pt.id} assignment={pt} />)}
            </div>
          )}
        </div>
      )}

      {tab === 'schedule' && (
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <div className="flex rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
              <button
                onClick={() => setViewMode('list')}
                className={`flex items-center gap-1.5 px-3 py-1.5 text-sm transition-colors ${
                  viewMode === 'list'
                    ? 'bg-primary-600 text-white'
                    : 'text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-700'
                }`}
              >
                <List className="w-3.5 h-3.5" />
                목록
              </button>
              <button
                onClick={() => setViewMode('calendar')}
                className={`flex items-center gap-1.5 px-3 py-1.5 text-sm transition-colors ${
                  viewMode === 'calendar'
                    ? 'bg-primary-600 text-white'
                    : 'text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-700'
                }`}
              >
                <CalendarDays className="w-3.5 h-3.5" />
                캘린더
              </button>
            </div>
            <button onClick={() => setIsScheduleOpen(true)} className="btn-primary flex items-center gap-2">
              <Plus className="w-4 h-4" />
              일정 등록
            </button>
          </div>

          {sessionsLoading ? (
            <div className="flex justify-center py-12">
              <div className="w-7 h-7 border-4 border-primary-600 border-t-transparent rounded-full animate-spin" />
            </div>
          ) : viewMode === 'calendar' ? (
            <div className="card p-4">
              <WeeklyCalendar
                sessions={sessions}
                onSelectSession={(s) => { setCompleteSession(s); setCompleteContent(''); setCompleteMemo(''); }}
              />
            </div>
          ) : sessions.length === 0 ? (
            <div className="card p-12 text-center">
              <CalendarDays className="w-12 h-12 text-gray-300 dark:text-gray-600 mx-auto mb-3" />
              <p className="text-gray-400">등록된 일정이 없습니다.</p>
            </div>
          ) : (
            <div className="space-y-5">
              {upcomingSessions.length > 0 && (
                <div>
                  <p className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase mb-2">예정 일정</p>
                  <div className="space-y-2">
                    {upcomingSessions.map(s => (
                      <div key={s.id} className="card p-4 flex items-start justify-between gap-3">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-0.5">
                            <span className="font-semibold text-sm text-gray-900 dark:text-white">{s.memberName}</span>
                            <span className="text-xs text-gray-400">· {s.trainerName} 트레이너</span>
                          </div>
                          <p className="text-xs text-gray-500 dark:text-gray-400">{s.productName}</p>
                          {s.notes && <p className="text-xs text-gray-400 mt-0.5 italic">"{s.notes}"</p>}
                        </div>
                        <div className="text-right flex-shrink-0 flex items-center gap-2">
                          <div>
                            <p className="text-sm font-bold text-primary-600 dark:text-primary-400">
                              {format(parseISO(s.scheduledAt), 'M/d (EEE)', { locale: ko })}
                            </p>
                            <p className="text-xs text-gray-500">{format(parseISO(s.scheduledAt), 'HH:mm')}</p>
                          </div>
                          <button
                            onClick={() => { setCompleteSession(s); setCompleteContent(''); setCompleteMemo(''); }}
                            className="text-xs px-2.5 py-1.5 rounded-lg bg-green-600 text-white hover:bg-green-700 transition-colors font-medium"
                          >
                            완료
                          </button>
                          <button
                            onClick={() => setCancelConfirm(s)}
                            className="p-1 text-gray-300 hover:text-red-500 transition-colors"
                          >
                            <X className="w-4 h-4" />
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {pastSessions.length > 0 && (
                <div>
                  <p className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase mb-2">지난 일정</p>
                  <div className="space-y-2">
                    {pastSessions.slice(0, 10).map(s => (
                      <div key={s.id} className="card p-4 flex items-start justify-between gap-3 opacity-60">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-0.5">
                            <span className="font-medium text-sm text-gray-900 dark:text-white">{s.memberName}</span>
                            <span className="text-xs text-gray-400">· {s.trainerName} 트레이너</span>
                            <span className={`text-xs px-1.5 py-0.5 rounded-full font-medium ${STATUS_BADGE[s.status] || STATUS_BADGE.scheduled}`}>
                              {STATUS_LABEL[s.status] || s.status}
                            </span>
                          </div>
                          <p className="text-xs text-gray-500 dark:text-gray-400">{s.productName}</p>
                        </div>
                        <div className="text-right flex-shrink-0">
                          <p className="text-sm text-gray-600 dark:text-gray-400">
                            {format(parseISO(s.scheduledAt), 'M/d (EEE)', { locale: ko })}
                          </p>
                          <p className="text-xs text-gray-400">{format(parseISO(s.scheduledAt), 'HH:mm')}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          <Modal isOpen={isScheduleOpen} onClose={() => setIsScheduleOpen(false)} title="PT 일정 등록">
            <PtScheduleForm
              assignments={ptAssignments}
              members={members}
              onClose={() => setIsScheduleOpen(false)}
              onSaved={loadSessions}
            />
          </Modal>

          <ConfirmDialog
            isOpen={!!cancelConfirm}
            title="일정 취소"
            message={`${cancelConfirm?.memberName} 회원의 일정을 취소하시겠습니까?`}
            onConfirm={handleCancelSession}
            onCancel={() => setCancelConfirm(null)}
          />

          <Modal
            isOpen={!!completeSession}
            onClose={() => setCompleteSession(null)}
            title="PT 수업 완료"
          >
            {completeSession && (
              <div className="space-y-4">
                <div className="p-3 bg-green-50 dark:bg-green-900/20 rounded-lg text-sm text-green-700 dark:text-green-300">
                  <span className="font-semibold">{completeSession.memberName}</span> ·{' '}
                  {completeSession.trainerName} 트레이너 ·{' '}
                  {format(parseISO(completeSession.scheduledAt), 'M/d (EEE) HH:mm', { locale: ko })}
                </div>
                <div>
                  <label className="label">운동 내용 <span className="text-gray-400 font-normal">(선택)</span></label>
                  <textarea
                    className="input resize-none"
                    rows={3}
                    placeholder="스쿼트 3세트, 데드리프트 3세트... (미입력 시 'PT 수업 완료'로 자동 기록)"
                    value={completeContent}
                    onChange={e => setCompleteContent(e.target.value)}
                  />
                </div>
                <div>
                  <label className="label">
                    회원 코멘트 <span className="text-gray-400 font-normal">(선택 · 회원 앱에 표시됩니다)</span>
                  </label>
                  <textarea
                    className="input resize-none"
                    rows={2}
                    placeholder="오늘 정말 열심히 하셨어요! 스쿼트 자세가 많이 좋아졌습니다 💪"
                    value={completeMemo}
                    onChange={e => setCompleteMemo(e.target.value)}
                  />
                </div>
                <div className="flex justify-end gap-2 pt-1">
                  <button type="button" onClick={() => setCompleteSession(null)} className="btn-secondary">취소</button>
                  <button
                    onClick={handleCompleteSession}
                    disabled={completing}
                    className="px-4 py-2 rounded-lg bg-green-600 text-white text-sm font-medium hover:bg-green-700 disabled:opacity-50 transition-colors"
                  >
                    {completing ? '처리 중...' : '완료 처리'}
                  </button>
                </div>
              </div>
            )}
          </Modal>
        </div>
      )}

      {tab === 'products' && (
        <div className="space-y-4">
          <div className="flex justify-end">
            <button onClick={() => { setEditingProduct(null); setIsProductFormOpen(true); }} className="btn-primary flex items-center gap-2">
              <Plus className="w-4 h-4" />
              PT 상품 등록
            </button>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {ptProducts.map(product => (
              <div key={product.id} className="card p-5">
                <div className="flex items-start justify-between mb-3">
                  <div className="w-10 h-10 rounded-xl bg-purple-100 dark:bg-purple-900/30 flex items-center justify-center">
                    <Dumbbell className="w-5 h-5 text-purple-600 dark:text-purple-400" />
                  </div>
                  <div className="flex gap-1">
                    <button onClick={() => { setEditingProduct(product); setIsProductFormOpen(true); }} className="p-1.5 text-gray-400 hover:text-blue-500 rounded-lg hover:bg-blue-50 dark:hover:bg-blue-900/20 transition-colors">
                      <Edit className="w-4 h-4" />
                    </button>
                    <button onClick={() => setDeleteProductConfirm(product)} className="p-1.5 text-gray-400 hover:text-red-500 rounded-lg hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors">
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
                <h3 className="font-semibold text-gray-900 dark:text-white">{product.name}</h3>
                <p className="text-2xl font-bold text-purple-600 dark:text-purple-400 mt-1">{formatPrice(product.price)}</p>
                <p className="text-sm text-gray-500 dark:text-gray-400 mt-2">{product.sessions}회{product.sessions > 0 ? ` · 회당 ${formatPrice(Math.round(product.price / product.sessions))}` : ''}</p>
              </div>
            ))}
          </div>
        </div>
      )}

      <Modal isOpen={isProductFormOpen} onClose={() => { setIsProductFormOpen(false); setEditingProduct(null); }} title={editingProduct ? 'PT 상품 수정' : 'PT 상품 등록'}>
        <PtProductForm product={editingProduct} onClose={() => { setIsProductFormOpen(false); setEditingProduct(null); }} />
      </Modal>

      <Modal isOpen={isAssignOpen} onClose={() => setIsAssignOpen(false)} title="PT 부여">
        <PtAssignForm onClose={() => setIsAssignOpen(false)} />
      </Modal>

      <ConfirmDialog
        isOpen={!!deleteProductConfirm}
        onClose={() => setDeleteProductConfirm(null)}
        onConfirm={async () => {
          if (deleteProductConfirm) {
            await deletePtProduct(deleteProductConfirm.id);
            setDeleteProductConfirm(null);
          }
        }}
        title="PT 상품 삭제"
        message={`"${deleteProductConfirm?.name}" 상품을 삭제하시겠습니까?`}
        confirmText="삭제"
        confirmColor="red"
      />
    </div>
  );
};

export default PT;
